import { Component, Inject, OnInit } from '@angular/core';
import moment from 'moment';
import { UtilsService } from '../../../shared/utils.service';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ApiRequestService } from '../../../shared/api-request.service';
import { AppService } from '../../../app.service';
import { AssetsService } from '../../assets.service';
import { AssetMaintenanceRecordsService } from '../asset-maintenance-records.service';
import { NgForm } from '@angular/forms';
import {
  NetworkedAccountsSelectorComponent
} from '../../../shared/networked-accounts-selector/networked-accounts-selector.component';
import { VendorsSelectorComponent } from '../../../shared/vendors-selector/vendors-selector.component';
import {
  NetworkedUsersSelectorComponent
} from '../../../shared/networked-users-selector/networked-users-selector.component';
import { faHardHat } from '@fortawesome/free-solid-svg-icons';
import { AssetMaintenanceRecordModel } from '../../../models/asset-maintenance-record.model';

@Component({
  selector: 'app-asset-maintenance-records-edit',
  templateUrl: './asset-maintenance-records-edit.component.html',
  styleUrls: ['./asset-maintenance-records-edit.component.scss']
})
export class AssetMaintenanceRecordsEditComponent implements OnInit {

  inProgress: boolean = false;
  asset_maintenance_record: AssetMaintenanceRecordModel = new AssetMaintenanceRecordModel();
  selectedFiles: File[] = [];
  faHardHat = faHardHat;

  typeOptions = this.utils.toOptions(this.assetMaintenanceRecordsService.TYPES);
  priorityOptions = this.utils.toOptions(this.assetMaintenanceRecordsService.PRIORITIES);
  statusOptions = this.utils.toOptions(this.assetMaintenanceRecordsService.STATUSES);
  timeSpentUnitOptions =  this.utils.toOptions(this.assetMaintenanceRecordsService.TIME_SPENT_UNITS);

  dueAtDateTime: moment.Moment | null;

  today: moment.Moment;

  costAmountInput: string | null = null;
  costAmountError: string = '';

  constructor(
    public utils: UtilsService,
    @Inject(MAT_DIALOG_DATA) public dialogData: any,
    private dialogRef: MatDialogRef<AssetMaintenanceRecordsEditComponent>,
    private api: ApiRequestService,
    public app: AppService,
    public assetsService: AssetsService,
    public assetMaintenanceRecordsService: AssetMaintenanceRecordsService
  ) { }

  ngOnInit() {
    const asset_id = this.dialogData?.asset_id;
    const asset_maintenance_record_id = this.dialogData?.asset_maintenance_record_id;

    this.today = moment()
      .tz(this.app.account.timezone);

    if (typeof asset_id === 'number' && asset_id > 0) {
      this.asset_maintenance_record.asset_id = asset_id;
    }

    if (typeof asset_maintenance_record_id === 'number' && asset_maintenance_record_id > 0) {
      this.find(asset_maintenance_record_id);
    }
  }

  /**
   * Finds an Asset Maintenance Record by its ID.
   *
   * @param {number} id - The ID of the Maintenance Record.
   * @return {void}
   */
  private find(id: number): void {
    this.api.makeRequest('get', `v2/assets/${this.asset_maintenance_record.asset_id}/maintenance-records/${id}`)
      .then((response) => {
        this.asset_maintenance_record = response.data as AssetMaintenanceRecordModel;

        this.asset_maintenance_record.user_ids = this.asset_maintenance_record.users ? this.asset_maintenance_record.users.map((user) => user.id) : [];

        // Convert the unix "due_at" timestamp to a moment date/time object using the account timezone.
        if ( this.asset_maintenance_record.due_at ) {
          this.dueAtDateTime = moment.unix(this.asset_maintenance_record.due_at).tz(this.app.account.timezone);
        }

        this.costAmountInput = this.assetsService.normalizeCurrencyAmount(
          this.asset_maintenance_record.total_cost,
          this.app.account.currency_code
        );

        this.onTimeSpentValueBlur();
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      });
  }

  /**
   * Handles form submission by checking if the form is valid and whether the submit button has already been pressed.
   * If the form is invalid, a form validation error message is displayed.
   *
   * @param {NgForm} form - The NgForm object representing the form to be submitted.
   * @param {boolean} [autoCloseDialog=true] - Flag indicating whether to automatically close the dialog after submission.
   * @return {void}
   */
  onSubmit(form: NgForm, autoCloseDialog: boolean = true): void {
    // Check if the button was already pressed.
    if ( this.inProgress ) {
      return;
    }

    // Check form validation.
    if ( !form.valid ) {
      this.utils.showFormValidationError('Please enter all required features.');
      return;
    }

    // Check Status validation.
    const validStatuses = Object.keys(this.assetMaintenanceRecordsService.STATUSES);
    if (!validStatuses.includes(this.asset_maintenance_record.status)) {
      this.utils.showFormValidationError('Please select a valid Status.');
      return;
    }

    // Check Priority validation.
    const validDPriorities = Object.keys(this.assetMaintenanceRecordsService.PRIORITIES);
    if (!validDPriorities.includes(this.asset_maintenance_record.priority)) {
      this.utils.showFormValidationError('Please select a valid Priority.');
      return;
    }

    // Check Type validation.
    if ( this.asset_maintenance_record.type !== 'other' ) {
      const validTypes = Object.keys(this.assetMaintenanceRecordsService.TYPES);

      if (!validTypes.includes(this.asset_maintenance_record.type)) {
        this.utils.showFormValidationError('Please select a valid Type.');
        return;
      }
    }

    // Check Type Other validation.
    if (
      this.asset_maintenance_record.type === 'other' &&
      (!this.asset_maintenance_record.type_other || !this.asset_maintenance_record.type_other.trim())
    ) {
      this.utils.showFormValidationError('Please enter the Other Type.');
      return;
    }

    // Check completion-specific field validation.
    if (this.asset_maintenance_record.status === 'completed') {
      // Check Time Spent validation.
      if (!this.isTimeSpentValid) {
        this.utils.showFormValidationError('Please enter a valid Time Spent value and unit.');
        return;
      }

      // Check Total Cost validation.
      if (this.costAmountError) {
        this.utils.showFormValidationError(this.costAmountError);
        return;
      }
    }

    this.inProgress = true;

    // Clear Type Other when the selected Type is not Other.
    if( this.asset_maintenance_record.type !== 'other' ){
      this.asset_maintenance_record.type_other = null;
    }

    // Clear cancellation-specific fields when the record is not cancelled.
    if ( this.asset_maintenance_record.status !== 'cancelled' ) {
      this.asset_maintenance_record.cancellation_details = null;
    }

    // Clear completion-specific fields when the record is not completed.
    if ( this.asset_maintenance_record.status !== 'completed' ) {
      this.asset_maintenance_record.total_cost = null;
      this.asset_maintenance_record.time_spent_unit = 'hours'; // default
      this.asset_maintenance_record.time_spent_value = null;
      this.asset_maintenance_record.completion_summary = null;
    }

    // Convert the selected Due At date and time to a unix timestamp.
    if ( this.dueAtDateTime && this.dueAtDateTime instanceof moment ) {
      const dueAtDateTime: moment.Moment = moment.tz(this.dueAtDateTime.format('M/D/YYYY, h:mm:ss a'), 'M/D/YYYY, h:mm:ss a', this.app.account.timezone);
      this.asset_maintenance_record.due_at = dueAtDateTime.unix();
    } else {
      this.asset_maintenance_record.due_at = null;
    }

    !this.asset_maintenance_record.id ? this.create(autoCloseDialog) : this.update(autoCloseDialog);
  }

  /**
   * Updates the Asset Maintenance Record with new data.
   *
   * @param {boolean} [autoCloseDialog=true] An optional param to indicate if the dialog should be closed or not.
   * @return void
   */
  private update(autoCloseDialog: boolean = true): void {
    this.api
      .makeRequest('put', `v2/assets/${this.asset_maintenance_record.asset_id}/maintenance-records/${this.asset_maintenance_record.id}`, this.asset_maintenance_record)
      .then((response) => {
        this.asset_maintenance_record = response.data as AssetMaintenanceRecordModel;

        this.onSuccess(`Updated: "${this.asset_maintenance_record.title}"`, autoCloseDialog);
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      })
      .finally(() => {
        this.inProgress = false;
      });
  }

  /**
   * Creates a new Asset Maintenance Record.
   *
   * @param {boolean} [autoCloseDialog=true] An optional param to indicate if the dialog should be closed or not.
   * @return {void}
   */
  private create(autoCloseDialog: boolean = true): void {
    this.api
      .makeRequest('post', `v2/assets/${this.asset_maintenance_record.asset_id}/maintenance-records`, this.asset_maintenance_record)
      .then((response) => {
        this.asset_maintenance_record = response.data as AssetMaintenanceRecordModel;

        this.onSuccess("New Asset Maintenance Record created.", autoCloseDialog);
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      })
      .finally(() => {
        // Upload files.
        this.uploadSelectedFiles(this.asset_maintenance_record.id, (): void => {
          this.inProgress = false;
        });
      });
  }

  /**
   * Handles the success scenario by showing a toast message with the given message and closing the dialog if autoCloseDialog is true.
   *
   * @param {string} message - The message to display in the toast.
   * @param {boolean} [autoCloseDialog=true] - Whether the dialog should be automatically closed. Default value is true.
   * @return {void}
   */
  private onSuccess(message: string, autoCloseDialog: boolean = true): void {
    this.utils.showToast(message);

    // Check if the dialog should be closed.
    if ( autoCloseDialog ) {
      this.dialogRef.close(this.asset_maintenance_record);
    }
  }

  /**
   * Determines if the Asset Maintenance Record due date and time is valid.
   *
   * The due date and time must:
   * - Not be null or undefined.
   * - Be a valid Moment date and time.
   *
   * @return {boolean} True if the due date and time is valid; otherwise, false.
   */
  get isDueAtValid(): boolean {
    return moment.isMoment(this.dueAtDateTime) && this.dueAtDateTime.isValid();
  }

  /**
   * Determines if the time spent on asset maintenance is valid.
   *
   * The method checks the following conditions:
   * - The time spent value must not be null, undefined, or negative.
   * - The time spent unit must be a recognized and valid unit of time.
   *
   * @return {boolean} True if the time spent value and unit are valid; otherwise, false.
   */
  get isTimeSpentValid(): boolean {
    const value = this.asset_maintenance_record.time_spent_value;
    const unit = this.asset_maintenance_record.time_spent_unit;

    if (value !== null && value !== undefined && value < 0) {
      return false;
    }

    return !!this.assetMaintenanceRecordsService.TIME_SPENT_UNITS[unit];
  }

  /**
   * Select contractor to associate with this asset maintenance schedule.
   *
   * @return {void}
   */
  onSelectContractor(): void {
    this.utils.showComponentDialog(
      NetworkedAccountsSelectorComponent,
      {
        selected: [this.asset_maintenance_record.contractor_account_id],
        multiple: false,
        can_clear_close: true
      },
      {
        width: '1200px'
      },
      (selected_contractor_account_id: number) => {
        if ( typeof selected_contractor_account_id != 'undefined' ) {
          this.asset_maintenance_record.contractor_account_id = selected_contractor_account_id > 0
            ? selected_contractor_account_id
            : null;
        }
      }
    );
  }

  /**
   * Select the vendor the asset maintenance schedule should be associated with.
   *
   * @return {void}
   */
  onSelectVendor(): void {
    this.utils.showComponentDialog(
      VendorsSelectorComponent,
      {
        selected: [this.asset_maintenance_record.vendor_id],
        multiple: false,
        required: false
      },
      {
        width: '1200px'
      },
      (selected_vendor_id: number) => {
        if ( typeof selected_vendor_id != 'undefined' ) {
          this.asset_maintenance_record.vendor_id = selected_vendor_id > 0 ? selected_vendor_id : null;
        }
      }
    );
  }

  /**
   * Select users to associate with this asset maintenance schedule.
   *
   * @return {void}
   */
  onSelectUsers(): void {
    this.utils.showComponentDialog(
      NetworkedUsersSelectorComponent,
      {
        selected: this.asset_maintenance_record.user_ids,
        multiple: true,
        visitors_from_all_sites: true
      },
      {
        width: '1200px'
      },
      (results: any) => {
        if (typeof results !== 'undefined') {
          // Set the newly selected ids.
          this.asset_maintenance_record.user_ids = results;
        }
      }
    );
  }

  /**
   * Retrieves the details of the output date and time based on the provided event.
   *
   * @param {any} event - The event object containing the output date and time details.
   * @return {void}
   */
  getOutputDueDateTimeDetails(event: any): void {
    this.dueAtDateTime = event;
  }

  /**
   * Uploads the selected files to the server for the specified resource ID.
   * Provides a callback function to be executed after the upload process completes.
   *
   * @param {number} id - The unique identifier of the target resource where files will be uploaded.
   * @param {function} [callback] - Optional callback function executed after the upload process is complete.
   * @return {void} This method does not return a value.
   */
  private uploadSelectedFiles(id: number, callback?: any): void {
    if ( id && this.selectedFiles.length > 0 ) {
      this.api.makeUploadRequest(`v2/file-manager/asset-maintenance-record/${id}`, this.selectedFiles)
        .then((response: any): void => {
          this.utils.showToast('Your files successfully uploaded.');
        })
        .finally((): void => {
          this.selectedFiles.length = 0;
          if ( typeof callback === 'function' ) {
            callback();
          }
        });
    } else {
      if ( typeof callback === 'function' ) {
        callback();
      }
    }
  }

  /**
   * If it is a new asset, add the files to the selected files array.
   * Existing asset files will be handled by the file manager.
   *
   * @param fileList
   */
  onSelectFiles(fileList: FileList): void {
    const files: File[] = Array.from(fileList);
    if ( files && files.length > 0 ) {
      this.selectedFiles.push(...files);
    }
  }

  /**
   * Validate and normalize cost amount on user input change.
   *
   * @param {string | number | null} value - The input value representing the cost amount.
   * @return {void}
   */
  onCostAmountChange(value: string | number | null): void {
    this.costAmountError = '';

    const stringValue = value === null || typeof value === 'undefined'
      ? null
      : String(value);

    if (!this.assetsService.isValidCurrencyAmount(stringValue, this.app.account.currency_code)) {
      const minorUnits = this.assetsService.getCurrencyMinorUnits(this.app.account.currency_code);

      this.costAmountError = `Total cost must be a valid number with up to ${minorUnits} decimal place${minorUnits === 1 ? '' : 's'}.`;
      return;
    }

    this.asset_maintenance_record.total_cost = stringValue === '' || stringValue === null
      ? null
      : Number(stringValue);
  }

  /**
   * Normalize the display value up to 3 decimal places on blur.
   *
   * @return {void}
   */
  onCostAmountBlur(): void {
    const normalized = this.assetsService.normalizeCurrencyAmount(
      this.costAmountInput,
      this.app.account.currency_code
    );

    this.costAmountInput = normalized;

    // Convert to number for model/API
    this.asset_maintenance_record.total_cost = normalized !== null ? Number(normalized) : null;
  }

  /**
   * Handles the time spent value by validating and rounding the value to two
   * decimal places. When called from the input blur event, the displayed value
   * is also normalized to remove unnecessary trailing zeros.
   *
   * @param {FocusEvent|null} event The optional input blur event.
   * @return {void} No value is returned.
   */
  onTimeSpentValueBlur(event: FocusEvent | null = null): void {
    const value = this.asset_maintenance_record.time_spent_value;

    if (value === null || value === undefined) {
      return;
    }

    const numericValue = Number(value);

    if (isNaN(numericValue)) {
      return;
    }

    const roundedValue = Math.round((numericValue + Number.EPSILON) * 100) / 100;

    this.asset_maintenance_record.time_spent_value = roundedValue;

    if ( event?.target ) {
      (event.target as HTMLInputElement).value = roundedValue.toString();
    }
  }

}
